import { useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { PageUrl } from '../../types/enums/pageUrl-enums';

const CHANNEL_NAME = 'bo-retro-board-single-tab';
const STORAGE_KEY = 'bo-retro-board-active-tab';

type TabMessage =
    | { type: 'claim'; tabId: string; at: number }
    | { type: 'ping'; tabId: string }
    | { type: 'pong'; tabId: string };

function readClaim(raw: string | null): Extract<TabMessage, { type: 'claim' }> | null {
    if (!raw) return null;
    try {
        const parsed = JSON.parse(raw) as TabMessage;
        if (parsed?.type === 'claim' && parsed.tabId && typeof parsed.at === 'number') {
            return parsed;
        }
    } catch {
        /* ignore */
    }
    return null;
}

/**
 * Aynı tarayıcıda Retro Board yalnızca bir sekmede aktif kalır.
 * Yeni sekme "buradan devam et"; eski sekmeler ana sayfaya yönlendirilir.
 */
export function useRetroSingleTabGuard(enabled = true): {
    isActiveTab: boolean;
    showTakeoverNotice: boolean;
    dismissTakeoverNotice: () => void;
} {
    const history = useHistory();
    const tabIdRef = useRef(
        typeof crypto !== 'undefined' && 'randomUUID' in crypto
            ? crypto.randomUUID()
            : `tab-${Date.now()}-${Math.random().toString(36).slice(2)}`
    );
    const claimedAtRef = useRef(Date.now());
    const [isActiveTab, setIsActiveTab] = useState(true);
    const [showTakeoverNotice, setShowTakeoverNotice] = useState(false);

    useEffect(() => {
        if (!enabled) return undefined;

        const tabId = tabIdRef.current;
        const at = claimedAtRef.current;
        const claim: Extract<TabMessage, { type: 'claim' }> = { type: 'claim', tabId, at };
        let redirected = false;
        let channel: BroadcastChannel | null = null;

        const publishClaim = () => {
            localStorage.setItem(STORAGE_KEY, JSON.stringify(claim));
            try {
                channel?.postMessage(claim);
            } catch {
                /* ignore */
            }
        };

        const redirectHome = () => {
            if (redirected) return;
            redirected = true;
            setIsActiveTab(false);
            history.replace(PageUrl.home.url);
        };

        const handleForeignClaim = (incoming: Extract<TabMessage, { type: 'claim' }>) => {
            if (!incoming || incoming.tabId === tabId) return;
            if (incoming.at >= at) {
                // Yeni sekmeye "başka sekme vardı" sinyali, sonra ana sayfa
                try {
                    channel?.postMessage({ type: 'pong', tabId });
                } catch {
                    /* ignore */
                }
                redirectHome();
            }
        };

        try {
            channel = new BroadcastChannel(CHANNEL_NAME);
            channel.onmessage = (event: MessageEvent<TabMessage>) => {
                const data = event.data;
                if (!data?.type) return;

                if (data.type === 'claim') {
                    handleForeignClaim(data);
                    return;
                }

                if (data.type === 'ping' && data.tabId !== tabId) {
                    try {
                        channel?.postMessage({ type: 'pong', tabId });
                    } catch {
                        /* ignore */
                    }
                    return;
                }

                if (data.type === 'pong' && data.tabId !== tabId) {
                    setShowTakeoverNotice(true);
                }
            };
        } catch {
            channel = null;
        }

        const onStorage = (event: StorageEvent) => {
            if (event.key !== STORAGE_KEY) return;
            const incoming = readClaim(event.newValue);
            if (incoming) handleForeignClaim(incoming);
        };

        window.addEventListener('storage', onStorage);

        // Önce canlı sekme var mı diye ping; sonra claim ile diğerlerini düşür
        try {
            channel?.postMessage({ type: 'ping', tabId });
        } catch {
            /* ignore */
        }

        const claimTimer = window.setTimeout(() => {
            publishClaim();
        }, 80);

        return () => {
            window.clearTimeout(claimTimer);
            window.removeEventListener('storage', onStorage);
            try {
                channel?.close();
            } catch {
                /* ignore */
            }
        };
    }, [enabled, history]);

    return {
        isActiveTab,
        showTakeoverNotice,
        dismissTakeoverNotice: () => setShowTakeoverNotice(false),
    };
}
